import React, { useEffect, useMemo } from 'react'
import { Switch, Redirect, useLocation } from 'react-router-dom'
import { SecureRoute } from '@okta/okta-react'
import { useAdvisorViewTemplates } from '../../../api/abundanceEngine'
import { ADVISOR_HOME_PATH } from '../../../constants'
import { useHideSideMenuEffect, useSetViewTitle } from '../../../redux/slices/appContext'
import LoadingView from '../LoadingView'
import AdvisorHomeTemplate from './AdvisorHomeTemplate'

const useGeneratedRoutes = (advisorViewTemplateConfig, defaultRedirect) => {
  return useMemo(() => {
    if (!advisorViewTemplateConfig) return []

    return advisorViewTemplateConfig.reduce((acc, config) => {
      const path = `/${ADVISOR_HOME_PATH}/${config.pathSegment}`
      // allow only one root path at a time
      if (config.hierarchyLevel === 0 && path !== defaultRedirect) {
        return acc
      }
      return [...acc, { key: config.pathSegment, config, path }]
    }, [])
  }, [advisorViewTemplateConfig, defaultRedirect])
}

const countChars = (str, char) => {
  return str.split(char).length - 1
}

const useDefaultRouteRedirect = (advisorViewTemplateConfig) => {
  const location = useLocation()

  return useMemo(() => {
    if (!advisorViewTemplateConfig) return {}

    const pathSegment = advisorViewTemplateConfig.reduce((acc, x) => {
      // we only care for templates meant to be used at root level
      if (x.hierarchyLevel !== 0) return acc

      // if no path segment matches current path we use default from acc
      if (location?.pathname.indexOf(x.pathSegment) === -1) return acc

      // use the path segment that matches the most with the current path
      if (!acc || countChars(acc, '/') < countChars(x.pathSegment, '/')) {
        return x.pathSegment
      }
      return acc
    }, advisorViewTemplateConfig.find((x) => x.hierarchyLevel === 0)?.pathSegment)

    return `/${ADVISOR_HOME_PATH}/${pathSegment}`
  }, [advisorViewTemplateConfig, location?.pathname])
}

const AdvisorHomeIndex = () => {
  useHideSideMenuEffect([])
  const { data, isLoading, error } = useAdvisorViewTemplates()
  const defaultRedirect = useDefaultRouteRedirect(data)
  const routes = useGeneratedRoutes(data, defaultRedirect)

  const location = useLocation()
  const setViewTitle = useSetViewTitle()

  useEffect(() => {
    const advisorViewTemplate = data?.find(
      ({ pathSegment }) => location.pathname.endsWith(pathSegment)
    )
    if (advisorViewTemplate) {
      setViewTitle(advisorViewTemplate?.name)
    }
    return () => {
      setViewTitle(null)
    }
  }, [location.pathname, data, setViewTitle])

  if (error) {
    return (
      <div>There was an error: {error?.toString()}</div>
    )
  }

  if (isLoading) {
    return <LoadingView height='100%' />
  }

  return (
    <div className='advisor-home'>
      <Switch>
        <SecureRoute
          exact
          path={[
            `/${ADVISOR_HOME_PATH}`,
            `/views/${ADVISOR_HOME_PATH}`,
            `/views/${ADVISOR_HOME_PATH}/*`
          ]}
        >
          <Redirect to={defaultRedirect} />
        </SecureRoute>
        {routes.map((r) => (
          <SecureRoute key={r.key} path={r.path}>
            <AdvisorHomeTemplate
              config={r.config}
              defaultRedirect={defaultRedirect}
            />
          </SecureRoute>
        ))}
        <SecureRoute path='*' root={ADVISOR_HOME_PATH}>
          <div>Not Found</div>
        </SecureRoute>
      </Switch>
    </div>
  )
}

export default AdvisorHomeIndex
